import { Facture, Devis } from "@/types";

/**
 * Generates the next invoice number based on existing invoices
 * Format: YYYYNNNN (e.g., 20180258, 20180259, ...)
 */
export function generateNextInvoiceNumber(toutesLesFactures: Facture[]): string {
    // LES AUTOFACTURES NE FONT PAS LA SUITE. Elles portent le numéro de leur
    // émetteur — TF1 Publicité numérote sur 8 chiffres (« 11600204 »), le
    // format même de cette suite : comptées ici, l'écran de création proposait
    // « 11600206 » pour la prochaine facture de la société (vu par Amine en
    // préproduction le 9 octobre 2026). Même règle que côté serveur, dans
    // « attribuerNumeroFacture » ; ici, elle couvre tous les écrans d'un coup.
    const existingInvoices = toutesLesFactures.filter((f) => !f.referenceExterne);
    if (existingInvoices.length === 0) {
        return "20180258";
    }

    // 1. Pattern avec tiret (ex: 2020-209)
    const dashedInvoices = existingInvoices
        .map(inv => inv.numero)
        .filter(num => /^\d{4}-\d+$/.test(num));

    if (dashedInvoices.length > 0) {
        const sortedDashed = dashedInvoices.sort((a, b) => {
            const [yearA, countA] = a.split("-").map(Number);
            const [yearB, countB] = b.split("-").map(Number);
            if (yearA !== yearB) return yearB - yearA; // Latest year first
            return countB - countA; // Highest count first
        });
        
        const latest = sortedDashed[0];
        const [year, count] = latest.split("-");
        return `${year}-${parseInt(count, 10) + 1}`;
    }

    // 2. Pattern standard 8 chiffres (ex: 20180258)
    const numericInvoices = existingInvoices
        .map(inv => inv.numero)
        .filter(num => /^\d{8}$/.test(num))
        .map(num => parseInt(num, 10))
        .filter(num => !isNaN(num));

    if (numericInvoices.length === 0) {
        return "20180258";
    }

    const maxNumber = Math.max(...numericInvoices);
    const nextNumber = maxNumber + 1;
    return nextNumber.toString().padStart(8, "0");
}

/**
 * Generates the next quote number based on existing quotes
 * Format: DEVYYYYNNNN (e.g., DEV20180001, DEV20180002, ...)
 */
export function generateNextQuoteNumber(existingQuotes: Devis[]): string {
    const currentYear = new Date().getFullYear().toString().substring(2); // "25" for 2025
    const startNumber = parseInt(`${currentYear}00001`); // 2500001, wait, user wants 8 digits? 22120349 is 8 digits.
    // 25 000001 is 8 digits.

    // Extract all numeric quote numbers that match the 8-digit pattern
    const numericQuotes = existingQuotes
        .map(quote => quote.numero)
        .filter(num => /^\d{8}$/.test(num))
        .map(num => parseInt(num, 10))
        .filter(num => !isNaN(num));

    if (numericQuotes.length === 0) {
        return `${currentYear}010001`; // Start at YY + 01 + 0001 -> 25010001
    }

    // Find the highest number and increment
    const maxNumber = Math.max(...numericQuotes);
    const nextNumber = maxNumber + 1;

    return nextNumber.toString();
}
